import commonLookupService from "../services/commonLookupService";

import {getLookupEntries,  saveLookupEntriesWithMeta} from "../services/db";
import {
  EVENT_STATUS_STORE_NAME,
  EVENT_TYPES_STORE_NAME,
  EVENT_GROUPS_STORE_NAME,
  VENUE_TYPES_STORE_NAME,
  TARGET_PARTICIPANTS_STORE_NAME,
  EVENT_OBJECTIVES_STORE_NAME,
  ORGANISER_EXPERIENCE_STORE_NAME,
  PARTICIPANT_DISABILITIES_STORE_NAME,
  GENDERS_STORE_NAME,
  RACES_STORE_NAME,
  AGE_GROUPS_STORE_NAME,
  QUALIFICATIONS_STORE_NAME,
  PARTICIPANT_OCCUPATIONS_STORE_NAME,
  EMPLOYMENT_SECTORS_STORE_NAME,
  EVENT_ROLES_STORE_NAME,
  ATTENDEE_MOTIVATIONS_STORE_NAME,
  LANGUAGES_STORE_NAME,
  REQUIRED_EVENTSAPP_STORES,
  FAQS_STORE_NAME,
  PROVINCES_DISTRICTS_STORE_NAME,
  PARTICIPANT_TYPES_STORE_NAME,
} from "../constants";
import { getFaqs } from "./eventsGuideService";

/**
 * Lookup configuration map.
 *
 * Maps a lookup key (store name) to its IndexedDB store and API fetcher.
 * Used by `getLookup` to resolve data source and caching behavior.
 *
 * @type {Record<string, {
 *   store: string,
 *   fetcher: () => Promise<{ status: number, data: Array }>
 * }>}
 */
export const LOOKUP_CONFIG = {
  [EVENT_STATUS_STORE_NAME]: {
    label: "Event status",
    fetcher: commonLookupService.getEventStatus,
  },

  [EVENT_TYPES_STORE_NAME]: {
    label: "Event types",
    fetcher: commonLookupService.getEventTypes,
  },

  [EVENT_GROUPS_STORE_NAME]: {
    label: "Event groups",
    fetcher: commonLookupService.getEventGroups,
  },

  [VENUE_TYPES_STORE_NAME]: {
    label: "Venue types",
    fetcher: commonLookupService.getVenueTypes,
  },

  [TARGET_PARTICIPANTS_STORE_NAME]: {
    label: "Target participants",
    fetcher: commonLookupService.getTargetParticipants,
  },

  [EVENT_OBJECTIVES_STORE_NAME]: {
    label: "Event objectives",
    fetcher: commonLookupService.getEventObjectives,
  },

  [ORGANISER_EXPERIENCE_STORE_NAME]: {
    label: "Organiser experience",
    fetcher: commonLookupService.getOrganiserExperience,
  },

  [PARTICIPANT_DISABILITIES_STORE_NAME]: {
    label: "Disability types",
    fetcher: commonLookupService.getDisability,
  },

  [PARTICIPANT_TYPES_STORE_NAME]: {
    label: "Disability types",
    fetcher: commonLookupService.getParticipantTypes,
  },

  [GENDERS_STORE_NAME]: {
    label: "Genders",
    fetcher: commonLookupService.getGenders,
  },

  [RACES_STORE_NAME]: {
    label: "Races",
    fetcher: commonLookupService.getRaces,
  },

  [AGE_GROUPS_STORE_NAME]: {
    label: "Age groups",
    fetcher: commonLookupService.getAgeGroup,
  },

  [LANGUAGES_STORE_NAME]: {
    label: "Languages",
    fetcher: commonLookupService.getLanguges,
  },

  [PROVINCES_DISTRICTS_STORE_NAME]: {
    label: "Province, district and town list",
    fetcher: commonLookupService.getProvincesDistricts,
  },

  [PARTICIPANT_OCCUPATIONS_STORE_NAME]: {
    label: "Participant occupations",
    fetcher: commonLookupService.getParticipantOccupations,
  },

  [EMPLOYMENT_SECTORS_STORE_NAME]: {
    label: "Employment sectors",
    fetcher: commonLookupService.getParticipantSectors,
  },

  [EVENT_ROLES_STORE_NAME]: {
    label: "Event roles",
    fetcher: commonLookupService.getParticipantRoles,
  },

  [ATTENDEE_MOTIVATIONS_STORE_NAME]: {
    label: "Attendee motivations",
    fetcher: commonLookupService.getParticipantMotivations,
  },

  [QUALIFICATIONS_STORE_NAME]: {
    label: "Post-matric qualifications",
    fetcher: commonLookupService.getPostMetricQualifications,
  },

  [FAQS_STORE_NAME]: {
    label: "Frequently Asked Questions",
    fetcher: getFaqs,
  },

  // [PROVINCES_DISTRICTS_TOWNS_STORE_NAME]: {
  //   label: "Province, district and town list",
  //   fetcher: commonLookupService.getProvincesDistrictTowns,
  // },

};

/**
 * Generic lookup loader with cache-first and falls back to network if empty.
 *
 * Delegates to {@link getLookup }.
 *
 * @param {string} key - Lookup key mapped in LOOKUP_CONFIG.
 * @returns {Promise<{ status: number, data: Array, source: 'network' | 'cache' | 'error' }>}
 */
const getCahedLookup = async (key) => {
  return getLookup(key, { useCache: true });
}

/**
 * Generic lookup loader with cache-first and network fallback.
 *
 * - Default: network-first, updates cache.
 * - useCache: cache-first, falls back to network if empty.
 * - Always falls back to cache on failure.
 *
 * @param {string} key - Lookup key mapped in LOOKUP_CONFIG.
 * @param {{ useCache?: boolean }} [options]
 * @returns {Promise<{ status: number, data: Array, source: 'network' | 'cache' | 'error' }>}
 */
const getLookup = async (key, { useCache = false } = {}) => {
  const config = LOOKUP_CONFIG[key];

  if (!config) {
    throw new Error(`[lookup] Unknown key: ${key}`);
  }

  const { fetcher } = config;
  const store = key;

  let networkError = null;

  // cache-first
  if (useCache) {
    try {
      const cached = await getLookupEntries(store);
      if (cached?.length) {
        return { status: 200, data: sortWithOtherLast(cached), source: "cache" };
      }
    } catch (err) {
      console.warn(`[lookup:${key}] cache failed`, err);
    }
  }

  // network
  try {
    const res = await fetcher();

    if (res?.status === 200) {
      const data = sortWithOtherLast(res.data) || [];

      if (data.length > 0) {
        saveLookupEntriesWithMeta(store, data);
      }

      return { status: 200, data, source: "network" };
    }

    networkError = new Error(`Status ${res?.status}`);
  } catch (err) {
    console.warn(`[lookup:${key}] network failed`, err);
    networkError = err;
  }

  // fallback → cache
  try {
    const cached = await getLookupEntries(store);

    return {
      status: 200,
      data: cached || [],
      source: "cache",
      fallbackFrom: "network-error",
    };
  } catch (cacheErr) {
    return {
      status: 500,
      data: [],
      source: "error",
      error: networkError || cacheErr,
    };
  }
};

export const sortWithOtherLast = (list = []) => {
  return [...list].sort((a, b) => {
    const isAOther = String(a?.value || "")
      .trim()
      .toLowerCase() === "other";

    const isBOther = String(b?.value || "")
      .trim()
      .toLowerCase() === "other";

    if (isAOther && !isBOther) return 1;
    if (!isAOther && isBOther) return -1;

    return 0;
  });
};

/**
 * Validates lookup configuration against DB schema and fetchers
 */
export const validateLookupConfig = () => {
  const configStores = Object.keys(LOOKUP_CONFIG);

  const errors = [];

  // Check missing fetchers
  configStores.forEach((store) => {
    const cfg = LOOKUP_CONFIG[store];

    if (!cfg?.fetcher || typeof cfg.fetcher !== "function") {
      errors.push(`[lookup] Missing or invalid fetcher for store: ${store}`);
    }
  });

  // Check config → DB mismatch
  configStores.forEach((store) => {
    if (!REQUIRED_EVENTSAPP_STORES.includes(store)) {
      errors.push(
        `[lookup] Store defined in LOOKUP_CONFIG but missing in DB schema: ${store}`
      );
    }
  });

  // Check DB → config mismatch
  REQUIRED_EVENTSAPP_STORES.forEach((store) => {
    if (!LOOKUP_CONFIG[store]) {
      console.warn(
        `errors.push [lookup] Store defined in DB schema but missing in LOOKUP_CONFIG: ${store}`
      ); 
    }
  });

  // Fail fast
  if (errors.length > 0) {
    console.error("LOOKUP CONFIG VALIDATION FAILED");
    errors.forEach((e) => console.error(e));

    throw new Error(
      `Lookup configuration validation failed with ${errors.length} error(s)`
    );
  }

  console.info("LOOKUP CONFIG VALIDATED");
};

/* ======================================
   DEV VALIDATION
   ====================================== */
if (process.env.NODE_ENV === "development") {
  validateLookupConfig();
}

const lookupService = {
  getLookup,
  getCahedLookup,
  LOOKUP_CONFIG,
}
export default lookupService;
